/**
 * Invoice Hooks
 *
 * Hooks for invoice management: listing, detail, generation, payment, mark-sent, void.
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { tryPrintPdfWindow, type PrintPdfResult } from '@/lib/printWindow';
import type { InvoicePayerPartyType } from '@/types';
import { queryKeys } from './queryKeys';
import { usePagedQuery, type PageSort } from './usePagedQuery';

export type { InvoicePayerPartyType };

// ── Types ──────────────────────────────────────────────────────────────

export interface InvoiceListItem {
  id: string;
  invoiceNumber: string;
  /** Null on a standalone invoice that has no work order (#2427). */
  bookingId: string | null;
  customerId: string;
  customerName: string;
  serviceType: string;
  vehicleReg: string;
  subtotalCents: number;
  taxCents: number;
  discountCents: number;
  totalCents: number;
  paidAmountCents: number;
  status: InvoiceStatus;
  /** Server-derived overdue flag (#1038): finalized + unpaid past due date. */
  isOverdue?: boolean;
  /** Denormalized method on the invoice (`mixed` for cash+card checkout). */
  paymentMethod?: string | null;
  /** Distinct methods from invoice_payments, excluding reversals. */
  paymentMethods?: string[];
  issueDate: string;
  dueDate: string | null;
  paidAt: string | null;
  /** System the invoice was imported from (`innovoice`); null when made here (#3366). */
  legacySource?: string | null;
}

export interface OverdueSummary {
  count: number;
  outstandingCents: number;
}

export type InvoiceStatus = 'draft' | 'sent' | 'paid' | 'partial' | 'overdue' | 'void' | 'refunded' | 'credited';

export interface InvoiceLineItem {
  id: string;
  lineType: 'labor' | 'part' | 'service' | 'discount' | 'tax' | 'surcharge';
  description: string;
  quantity: number;
  unit: string;
  unitPriceCents: number;
  discountPercent: number;
  lineTotalCents: number;
  taxable: boolean;
  taxPercent: number | null;
  groupName?: string | null;
  groupSortOrder?: number;
  /** Technician remarks for this task (#1754 / #2645). */
  groupNotes?: string | null;
  sortOrder: number;
}

/** One VAT-rate group in the statutory per-rate breakdown (#1033). */
export interface TaxBreakdownEntry {
  taxPercent: number;
  netCents: number;
  taxCents: number;
  grossCents: number;
}

export interface InvoicePaymentEntry {
  id: string;
  invoiceId?: string;
  amountCents: number;
  paymentMethod: string;
  paymentReference: string | null;
  paidAt: string;
  recordedBy: string | null;
  note: string | null;
  cashReceivedCents?: number | null;
  cashChangeCents?: number | null;
  receiptId?: string | null;
  receiptNumber?: string | null;
  receiptEmail?: string | null;
  receiptDeliveryStatus?: string | null;
}

export interface InvoiceDetail extends InvoiceListItem {
  taxPercent: number;
  discountReason: string | null;
  /** Optimistic-lock counter; sent back on every draft edit (#1035). */
  version: number;
  paymentMethod: string | null;
  paymentReference: string | null;
  customerReference: string | null;
  paymentReferenceNumber: string | null;
  sentAt: string | null;
  finalizedAt: string | null;
  voidedAt: string | null;
  voidReason: string | null;
  sourceInvoiceId: string | null;
  notes: string | null;
  termsAndConditions: string | null;
  customerPhone: string | null;
  customerEmail: string | null;
  /** Live bill-to party (work-order party or standalone payer). */
  billingParty?: {
    partyType: 'person' | 'company' | null;
    personId: string | null;
    companyId: string | null;
  } | null;
  createdAt: string;
  updatedAt: string;
  lineItems: InvoiceLineItem[];
  taxBreakdown: TaxBreakdownEntry[];
  payments: InvoicePaymentEntry[];
  deliveries: InvoiceDeliveryEntry[];
  invoiceType: 'invoice' | 'credit_note';
  refunds: InvoiceRefundEntry[];
  creditNotes: CreditNoteEntry[];
  sveaPaymentRequest?: SveaPaymentRequest | null;
}

export type SveaPaymentRequestStatus = 'created' | 'paid' | 'cancelled' | 'error' | 'expired';

export interface SveaPaymentRequest {
  id: string;
  invoiceId: string;
  pmtId: string;
  amountCents: number;
  status: SveaPaymentRequestStatus;
  paymentUrl: string;
  sveaPaymentMethod: string | null;
  sveaReturnCode: string | null;
  expiresAt: string | null;
  lastSentAt: string | null;
  lastSentVia: 'email' | 'whatsapp' | null;
  lastSentTo: string | null;
  createdAt: string;
}

/** A refund record shown in the invoice detail (#1037). */
export interface InvoiceRefundEntry {
  id: string;
  amountCents: number;
  reason: string | null;
  method: string | null;
  status: string;
  externalReference: string | null;
  refundedAt: string;
}

/** Open Svea refund attempts are not completed customer refunds (#2812). */
export const OPEN_SVEA_REFUND_STATUSES = ['pending', 'pending_manual_transfer'] as const;

export type SveaRefundAttemptStatus =
  | 'pending'
  | 'pending_manual_transfer'
  | 'accepted'
  | 'rejected';

export type SveaRefundAttemptPurpose = 'refund' | 'void_cancel';

export type SveaRefundAction = 'CANCEL' | 'REFUND_AFTER_SETTLEMENT';

export type InvoiceRefundOutcomeStatus = 'completed' | 'pending' | 'pending_manual_transfer';

/** Bank transfer the merchant must make so Svea can refund the payer (#2812). */
export interface SveaMerchantTransfer {
  recipientName: string;
  iban: string;
  reference: string;
  amountCents: number;
}

/**
 * GET /invoices/:id/svea-refunds row. Fields named in #2812 are required;
 * extras from the server DTO stay optional.
 */
export interface SveaRefundAttemptDto {
  id: string;
  pmtId: string;
  amountCents: number;
  status: SveaRefundAttemptStatus;
  action: SveaRefundAction;
  purpose: SveaRefundAttemptPurpose;
  merchantTransfer: SveaMerchantTransfer | null;
  invoiceRefundId: string | null;
  invoiceId?: string;
  cancelId?: string;
  originalAmountCents?: number;
  sveaReturnCode?: string | null;
}

export interface SveaRefundAttemptsResponse {
  attempts: SveaRefundAttemptDto[];
}

export interface RefundPaymentResponse {
  success: true;
  status?: InvoiceRefundOutcomeStatus;
  refundId?: string;
  sveaRefundAttempt?: SveaRefundAttemptDto;
  merchantTransfer?: SveaMerchantTransfer;
}

export interface RefundPaymentMutationInput {
  invoiceId: string;
  workOrderId?: string;
  amountCents: number;
  reason?: string;
  method?: string;
  pmtId?: string;
}

export function sveaRefundAttemptsQueryKey(invoiceId: string) {
  return [...queryKeys.invoices.detail(invoiceId), 'svea-refunds'] as const;
}

export function isOpenSveaRefundStatus(
  status: string,
): status is (typeof OPEN_SVEA_REFUND_STATUSES)[number] {
  return status === 'pending' || status === 'pending_manual_transfer';
}

/** Ledger or Svea attempt that has actually returned money to the customer. */
export function isCompletedCustomerRefundStatus(status: string): boolean {
  return status === 'completed' || status === 'accepted';
}

/**
 * Classify POST /invoices/:id/refund. 201 completed has refundId; 202 pending
 * and 202 pending_manual_transfer must not be treated as a completed refund.
 */
export function classifyInvoiceRefundResponse(
  body: RefundPaymentResponse,
): InvoiceRefundOutcomeStatus {
  if (body.status === 'pending_manual_transfer') return 'pending_manual_transfer';
  if (body.status === 'pending') return 'pending';
  if (body.sveaRefundAttempt && isOpenSveaRefundStatus(body.sveaRefundAttempt.status)) {
    return body.sveaRefundAttempt.status;
  }
  if (body.merchantTransfer) return 'pending_manual_transfer';
  return 'completed';
}

/** A credit note issued against this invoice (#1037). */
export interface CreditNoteEntry {
  id: string;
  invoiceNumber: string;
  totalCents: number;
  status: string;
  issueDate: string;
  notes: string | null;
}

/** A line selected for crediting (positive amounts sent to the API). */
export interface CreditNoteLineInput {
  lineType: 'labor' | 'part' | 'service' | 'surcharge' | 'discount';
  description: string;
  quantity: number;
  unit: string;
  unitPriceCents: number;
  discountPercent: number;
  lineTotalCents: number;
  taxable: boolean;
  taxPercent: number | null;
  groupName?: string | null;
  groupSortOrder?: number;
  groupNotes?: string | null;
}

export type InvoiceDeliveryStatus = 'queued' | 'processing' | 'sent' | 'failed';

/**
 * Maventa's own state of an e-invoice (#3070): handed over, delivered,
 * confirmed by the recipient's channel, or failed. Unknown values pass through.
 */
export type EinvoiceProviderStatus = 'PENDING' | 'PROCESSING' | 'DELIVERED' | 'DELIVERY_CONFIRMED' | 'FAILED';

/** One audited delivery attempt of the finalized invoice (#1036, #3071). */
export interface InvoiceDeliveryEntry {
  id: string;
  /** `email`, `einvoice` or `reminder`. */
  channel: string;
  /** Null for an e-invoice. */
  recipientEmail: string | null;
  /** E-invoice address with scheme, e.g. `0216:003712345678`. */
  recipientEia?: string | null;
  recipientOperator?: string | null;
  providerStatus?: EinvoiceProviderStatus | (string & {}) | null;
  status: InvoiceDeliveryStatus;
  attemptCount: number;
  sentAt: string | null;
  providerMessageId: string | null;
  errorMessage: string | null;
  createdAt: string;
}

/** A line as sent to the draft-edit endpoint (full replacement). */
export interface DraftLineInput {
  lineType: 'labor' | 'part' | 'service' | 'surcharge' | 'discount';
  description: string;
  quantity: number;
  unit: string;
  unitPriceCents: number;
  discountPercent: number;
  lineTotalCents: number;
  taxable: boolean;
  taxPercent: number | null;
  groupName?: string | null;
  groupSortOrder?: number;
  groupNotes?: string | null;
}

/**
 * Group metadata copied onto a draft-edit or credit-note line so a save
 * cannot drop technician remarks that the invoice already holds (#2645).
 */
export function invoiceLineGroupFields(line: {
  groupName?: string | null;
  groupSortOrder?: number;
  groupNotes?: string | null;
}): Pick<DraftLineInput, 'groupName' | 'groupSortOrder' | 'groupNotes'> {
  return {
    groupName: line.groupName ?? null,
    ...(line.groupSortOrder !== undefined ? { groupSortOrder: line.groupSortOrder } : {}),
    groupNotes: line.groupNotes ?? null,
  };
}

export function toDraftLineInput(line: InvoiceLineItem): DraftLineInput {
  const lineType = line.lineType === 'tax' ? 'service' : line.lineType;
  return {
    lineType,
    description: line.description,
    quantity: line.quantity,
    unit: line.unit,
    unitPriceCents: line.unitPriceCents,
    discountPercent: line.discountPercent,
    lineTotalCents: line.lineTotalCents,
    taxable: line.taxable,
    taxPercent: line.taxPercent,
    ...invoiceLineGroupFields(line),
  };
}

export function toCreditNoteLineInput(line: InvoiceLineItem): CreditNoteLineInput {
  const lineType = line.lineType === 'tax' ? 'service' : line.lineType;
  return {
    lineType,
    description: line.description,
    quantity: line.quantity,
    unit: line.unit,
    unitPriceCents: line.unitPriceCents,
    discountPercent: line.discountPercent,
    lineTotalCents: line.lineTotalCents,
    taxable: line.taxable,
    taxPercent: line.taxPercent,
    ...invoiceLineGroupFields(line),
  };
}

export interface UpdateDraftInvoiceData {
  invoiceId: string;
  workOrderId?: string;
  expectedVersion: number;
  lines?: DraftLineInput[];
  discountCents?: number;
  discountReason?: string | null;
  dueDate?: string | null;
  notes?: string | null;
  termsAndConditions?: string | null;
  customerReference?: string | null;
  paymentReferenceNumber?: string | null;
}

export interface UpdateDraftInvoiceResponse {
  success: true;
  id: string;
  version: number;
  subtotalCents: number;
  taxCents: number;
  discountCents: number;
  totalCents: number;
  taxBreakdown: TaxBreakdownEntry[];
}

export type InvoiceDeliveryChannel = 'email' | 'einvoice' | 'manual';

export interface GenerateInvoiceData {
  workOrderId: string;
  taxPercent?: number;
  discountCents?: number;
  discountReason?: string;
  dueDate?: string;
  notes?: string;
  termsAndConditions?: string;
  customerReference?: string;
  paymentReferenceNumber?: string;
  lines?: DraftLineInput[];
  /** Finalize and (for email or e-invoice) queue delivery in the same request (#3112, #3071). */
  issue?: {
    channel: 'email' | 'einvoice' | 'manual';
    recipientEmail?: string;
    /** E-invoice address picked from Maventa's registry; default is the frozen recipient's. */
    recipientEia?: string;
    recipientOperator?: string;
  };
}

export interface GenerateInvoiceResponse {
  id: string;
  invoiceNumber: string;
  subtotalCents: number;
  taxCents: number;
  discountCents: number;
  totalCents: number;
  status: string;
  issueDate: string;
  dueDate: string | null;
  /** Queued email delivery when the request issued the invoice (#3112). */
  delivery?: { deliveryId: string; status: string } | null;
}

/** Explicit bill-to party for POST /invoices. Omit the unused id field. */
export interface InvoicePayerInput {
  partyType: InvoicePayerPartyType;
  personId?: string;
  companyId?: string;
}

export interface CreateStandaloneInvoiceInput {
  payer: InvoicePayerInput;
  lines: DraftLineInput[];
  discountCents?: number;
  discountReason?: string | null;
  dueDate?: string | null;
  paymentTermsDays?: number;
  notes?: string | null;
  termsAndConditions?: string | null;
  customerReference?: string | null;
  taxPercent?: number;
}

export interface CreateStandaloneInvoiceResponse {
  id: string;
  invoiceNumber: string;
  bookingId: null;
  status: 'draft';
  version: number;
  payer: {
    partyType: InvoicePayerPartyType;
    personId: string | null;
    companyId: string | null;
  };
  subtotalCents: number;
  taxCents: number;
  discountCents: number;
  totalCents: number;
  taxPercent: number;
  issueDate: string;
  dueDate: string | null;
  taxBreakdown: TaxBreakdownEntry[];
}

/**
 * Spread onto invoice mutations so a free invoice does not send a fabricated
 * work-order id (#2427).
 */
export function invoiceWorkOrderRef(
  bookingId: string | null | undefined,
): { workOrderId: string } | Record<string, never> {
  if (!bookingId) return {};
  return { workOrderId: bookingId };
}

export interface RecordPaymentData {
  invoiceId: string;
  workOrderId?: string;
  amountCents: number;
  paymentMethod: 'cash' | 'card' | 'invoice' | 'bank_transfer' | 'other';
  paymentReference?: string;
  /** Value date (ISO) of the payment, distinct from record time (#1038). */
  paymentDate?: string;
}

export type CheckoutTender =
  | { method: 'cash'; amountCents: number; cashReceivedCents: number }
  | { method: 'card' | 'other'; amountCents: number; paymentReference: string };

export interface CheckoutInvoiceData {
  invoiceId: string;
  workOrderId?: string;
  /** Omitted when the customer wants the receipt on paper only (#3348). */
  receiptEmail?: string;
  tenders: CheckoutTender[];
  idempotencyKey: string;
}

export interface CheckoutWorkOrderData {
  workOrderId: string;
  /** Omitted when the customer wants the receipt on paper only (#3348). */
  receiptEmail?: string;
  tenders: CheckoutTender[];
  idempotencyKey: string;
  taxPercent?: number;
  discountCents?: number;
  discountReason?: string;
  dueDate?: string;
  notes?: string;
  termsAndConditions?: string;
  customerReference?: string;
  paymentReferenceNumber?: string;
  lines?: DraftLineInput[];
}

export interface CheckoutInvoiceResponse {
  success: true;
  invoiceId: string;
  paidAmountCents: number;
  totalCents: number;
  remainingCents: number;
  status: string;
  paymentBatchId: string;
  receipt: {
    id: string;
    receiptNumber: string;
    issuedAt: string;
    /** Null for a paper-only receipt (#3348); then deliveryStatus is 'not_requested'. */
    recipientEmail: string | null;
    deliveryStatus: string;
  };
}

export interface InvoiceFilters {
  [key: string]: string | undefined;
  /** Free text: customer, vehicle, work order or invoice number (#2984). */
  q?: string;
  status?: string;
  dateFrom?: string;
  dateTo?: string;
  customerId?: string;
  paymentMethod?: string;
}

// ── Work Order Invoice (from existing endpoint) ────────────────────────

export interface WorkOrderInvoice {
  id: string;
  invoiceNumber: string;
  bookingId: string;
  customerId: string;
  customerEmail?: string | null;
  issueDate: string;
  dueDate: string | null;
  subtotalCents: number;
  surchargeCents?: number;
  taxPercent: number;
  taxCents: number;
  discountCents: number;
  discountReason: string | null;
  totalCents: number;
  paidAmountCents: number;
  status: InvoiceStatus;
  paymentMethod: string | null;
  paymentReference: string | null;
  paidAt: string | null;
  voidedAt?: string | null;
  voidReason?: string | null;
  sourceInvoiceId?: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  payments?: InvoicePaymentEntry[];
}

// ── Hooks ──────────────────────────────────────────────────────────────

/**
 * List invoices with filters
 */
/**
 * Invoice columns the list endpoint will ORDER BY (#2502). Customer name and
 * payment method are not in this set: the API rejects them.
 */
export type InvoiceListSortField =
  | 'invoiceNumber'
  | 'totalCents'
  | 'paidAmountCents'
  | 'status'
  | 'dueDate'
  | 'issueDate';

export type InvoiceSortDirection = 'asc' | 'desc';

export interface InvoicePage {
  /** Rows per request. The server caps this; see INVOICE_LIST_MAX_LIMIT. */
  limit: number;
  offset: number;
  /** Omitted sort keeps the server default: issue date descending, then id. */
  sortBy?: InvoiceListSortField;
  sortDir?: InvoiceSortDirection;
}

export interface InvoiceListResponse {
  data: InvoiceListItem[];
  /** Rows matching the filters, across all pages — what the pager counts. */
  total: number;
  limit: number;
  offset: number;
}

/**
 * List invoices with filters, one page at a time.
 *
 * The list used to fetch every invoice. A tenant with 14 065 of them waited on
 * a joined query over the whole table plus one lookup per work order (#2479),
 * so the page size is now part of the request. Sort is part of it too (#2502):
 * the cache key includes the column and direction, or a new sort would reuse
 * the previous page's rows.
 */
export function useInvoices(filters: InvoiceFilters = {}, page?: InvoicePage) {
  return useQuery({
    queryKey: queryKeys.invoices.list({
      ...filters,
      ...(page
        ? {
            limit: String(page.limit),
            offset: String(page.offset),
            ...(page.sortBy ? { sortBy: page.sortBy } : {}),
            ...(page.sortDir ? { sortDir: page.sortDir } : {}),
          }
        : {}),
    }),
    queryFn: () => {
      const params = new URLSearchParams();
      if (filters.status) params.set('status', filters.status);
      if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.set('dateTo', filters.dateTo);
      if (filters.customerId) params.set('customerId', filters.customerId);
      if (filters.paymentMethod) params.set('paymentMethod', filters.paymentMethod);
      if (page) {
        params.set('limit', String(page.limit));
        params.set('offset', String(page.offset));
        if (page.sortBy) params.set('sortBy', page.sortBy);
        if (page.sortDir) params.set('sortDir', page.sortDir);
      }
      const qs = params.toString();
      return api.get<InvoiceListResponse>(`/invoices${qs ? `?${qs}` : ''}`);
    },
    staleTime: 30 * 1000,
    // Keeps the current page on screen while the next one loads, instead of
    // collapsing the table to its empty state on every page change.
    placeholderData: (previous) => previous,
  });
}

/**
 * Page-number invoice list on the shared paging hook (#2744).
 *
 * The invoice route predates the shared offset envelope and answers
 * `{ data, total, limit, offset }`; this maps it to the rows-and-total shape
 * usePagedQuery expects. Omitted sort keeps the server default: issue date
 * descending.
 *
 * @param initialSort Sort for the first render, e.g. read from `?sort=` so
 * Back restores the list order (#4100). Later changes go through `setSort`.
 */
export function useInvoicesPage(filters: InvoiceFilters = {}, initialSort?: PageSort) {
  return usePagedQuery<InvoiceListItem, InvoiceFilters>({
    queryKey: [...queryKeys.invoices.lists(), 'page'],
    filters,
    defaultSort: initialSort ?? { sortBy: 'issueDate', sortDir: 'desc' },
    staleTime: 30 * 1000,
    fetchPage: async ({ filters: pageFilters, limit, offset, sortBy, sortDir }) => {
      const params = new URLSearchParams();
      if (pageFilters.q) params.set('q', pageFilters.q);
      if (pageFilters.status) params.set('status', pageFilters.status);
      if (pageFilters.dateFrom) params.set('dateFrom', pageFilters.dateFrom);
      if (pageFilters.dateTo) params.set('dateTo', pageFilters.dateTo);
      if (pageFilters.customerId) params.set('customerId', pageFilters.customerId);
      if (pageFilters.paymentMethod) params.set('paymentMethod', pageFilters.paymentMethod);
      params.set('limit', String(limit));
      params.set('offset', String(offset));
      if (sortBy) params.set('sortBy', sortBy);
      if (sortDir) params.set('sortDir', sortDir);
      const res = await api.get<InvoiceListResponse>(`/invoices?${params.toString()}`);
      return { data: res.data, total: res.total };
    },
  });
}

/** Count + outstanding total of currently overdue invoices (#1038). */
export function useOverdueSummary() {
  return useQuery({
    queryKey: [...queryKeys.invoices.all, 'overdue-summary'],
    queryFn: () => api.get<OverdueSummary>('/invoices/overdue-summary'),
    staleTime: 60 * 1000,
  });
}

// ── Accounting export (#1039) ──────────────────────────────────────────

export interface ExportPreview {
  invoiceCount: number;
  totalNetCents: number;
  totalTaxCents: number;
  totalGrossCents: number;
  invoices: { id: string; invoiceNumber: string; invoiceType: string; netCents: number; taxCents: number; grossCents: number }[];
}

export interface ExportBatchListItem {
  id: string;
  format: string;
  status: string;
  invoiceCount: number;
  totalNetCents: number;
  totalTaxCents: number;
  totalGrossCents: number;
  fileSha256: string | null;
  /** Download name; v1 batches (before #4019) are `export-<id>.csv`. */
  fileName: string;
  /** Tenant-local accounting period, YYYY-MM-DD; null on v1 batches. */
  periodFrom: string | null;
  periodTo: string | null;
  createdAt: string;
  completedAt: string | null;
  /** Set when the batch was removed and its invoices released (#4056). */
  cancelledAt: string | null;
}

/** Optional invoice-issue-date window for accounting export (#2884). YYYY-MM-DD; both ends inclusive. */
export interface InvoiceExportFilters {
  dateFrom?: string;
  dateTo?: string;
}

function invoiceExportParams(filters?: InvoiceExportFilters): Record<string, string> {
  const params: Record<string, string> = {};
  if (filters?.dateFrom) params.dateFrom = filters.dateFrom;
  if (filters?.dateTo) params.dateTo = filters.dateTo;
  return params;
}

/** Preview what an export batch would contain right now (#1039, date window #2884). */
export function useExportPreview(filters: InvoiceExportFilters = {}) {
  const params = invoiceExportParams(filters);
  return useQuery({
    queryKey: [...queryKeys.invoices.all, 'export-preview', params],
    queryFn: () => api.get<ExportPreview>('/invoice-exports/preview', { params }),
    staleTime: 15 * 1000,
  });
}

/** List accounting export batches (#1039). */
export function useExportBatches() {
  return useQuery({
    queryKey: [...queryKeys.invoices.all, 'export-batches'],
    queryFn: () => api.get<{ data: ExportBatchListItem[] }>('/invoice-exports'),
    staleTime: 15 * 1000,
  });
}

/** Build + complete an export batch (#1039, date window #2884). */
export function useCreateExportBatch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (filters: InvoiceExportFilters = {}) =>
      api.post<{ success: true; batchId: string; invoiceCount: number }>(
        '/invoice-exports',
        invoiceExportParams(filters),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [...queryKeys.invoices.all, 'export-batches'] });
      void queryClient.invalidateQueries({ queryKey: [...queryKeys.invoices.all, 'export-preview'] });
    },
  });
}

/** Remove a completed batch; its invoices return to the next export (#4056). */
export function useCancelExportBatch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (batchId: string) =>
      api.delete<{ success: true; batchId: string; releasedInvoiceCount: number }>(`/invoice-exports/${batchId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [...queryKeys.invoices.all, 'export-batches'] });
      void queryClient.invalidateQueries({ queryKey: [...queryKeys.invoices.all, 'export-preview'] });
    },
  });
}

/** Download a completed batch's CSV (#1039). */
export async function downloadExportCsv(batch: Pick<ExportBatchListItem, 'id' | 'fileName'>): Promise<void> {
  const blob = await api.getBlob(`/invoice-exports/${batch.id}/csv`);
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = batch.fileName;
  link.click();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
}

/** Queue an idempotent payment reminder for an overdue invoice (#1038). */
export function useRemindInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ invoiceId, workOrderId: _workOrderId }: { invoiceId: string; workOrderId?: string }) =>
      api.post<{ success: true; deliveryId: string; status: string; alreadyQueued: boolean }>(
        `/invoices/${invoiceId}/remind`, {},
      ),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.detail(variables.invoiceId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all });
    },
  });
}

/**
 * Get invoice detail with line items
 */
export function useInvoiceDetail(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.invoices.detail(id ?? ''),
    queryFn: () => api.get<InvoiceDetail>(`/invoices/${id}`),
    enabled: !!id,
  });
}

/**
 * Get invoice for a specific work order (from existing work-orders endpoint)
 */
export function useWorkOrderInvoice(workOrderId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.invoices.forWorkOrder(workOrderId),
    queryFn: () => api.get<WorkOrderInvoice>(`/work-orders/${workOrderId}/invoice`),
    enabled,
    retry: false,
  });
}

export interface InvoicePreviewLine {
  lineType: 'labor' | 'part' | 'service' | 'surcharge' | 'discount';
  description: string;
  quantity: number;
  unit: string;
  unitPriceCents: number;
  discountPercent: number;
  lineTotalCents: number;
  taxable: boolean;
  taxPercent: number | null;
  groupName?: string | null;
  groupSortOrder?: number;
  groupNotes?: string | null;
  /**
   * Combined labour line only (#2638): the service whose steps the line sums
   * (#2375). Resolves the line to its source without description matching.
   */
  bookingServiceId?: string;
  /** Every labour step summed into the combined line, in step order (#2638). */
  laborItemIds?: string[];
}

export interface InvoicePreviewData {
  subtotalCents: number;
  surchargeCents: number;
  taxCents: number;
  discountCents: number;
  totalCents: number;
  lineItems: InvoicePreviewLine[];
  taxBreakdown: TaxBreakdownEntry[];
  /** Bill-to party of the work order (#3071); null without one. */
  billingRecipient?: {
    partyType: 'person' | 'company' | null;
    personId: string | null;
    companyId: string | null;
  } | null;
}

export interface WorkOrderInvoiceHistoryEntry {
  id: string;
  invoiceNumber: string;
  status: InvoiceStatus;
  totalCents: number;
  paidAmountCents: number;
  issueDate: string;
  voidedAt: string | null;
  voidReason: string | null;
  createdAt: string;
}

/**
 * Full invoice history for a work order — includes voided / refunded /
 * credited so the UI can show "previous invoices" even when no active
 * invoice is in flight.
 */
export function useWorkOrderInvoicesHistory(workOrderId: string, enabled = true) {
  return useQuery({
    queryKey: ['workOrderInvoices', workOrderId],
    queryFn: () => api.get<WorkOrderInvoiceHistoryEntry[]>(`/work-orders/${workOrderId}/invoices`),
    enabled,
  });
}

/**
 * Live preview of an invoice — fetches the totals + line items the
 * BillingService would generate without persisting. Disabled until the
 * user opens the dialog so a closed dialog doesn't poll the API.
 */
export function useInvoicePreview(
  workOrderId: string,
  taxPercent: number,
  discountCents: number,
  enabled: boolean,
) {
  return useQuery({
    queryKey: [...queryKeys.invoicePreview(workOrderId), taxPercent, discountCents],
    queryFn: () => {
      const params = new URLSearchParams();
      params.set('taxPercent', String(taxPercent));
      if (discountCents > 0) params.set('discountCents', String(discountCents));
      return api.get<InvoicePreviewData>(
        `/work-orders/${workOrderId}/invoice/preview?${params.toString()}`,
      );
    },
    enabled,
    retry: false,
    staleTime: 0,
  });
}

/**
 * Generate invoice for work order
 */
export function useGenerateInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ workOrderId, ...body }: GenerateInvoiceData) =>
      // Idempotency-Key so a transparent retry (e.g. after token refresh) can't
      // create a second invoice; the server replays the first response (#1034).
      api.post<GenerateInvoiceResponse>(`/work-orders/${workOrderId}/invoice`, body, {
        headers: { 'Idempotency-Key': crypto.randomUUID() },
      }),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.detail(variables.workOrderId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.lists() });
    },
  });
}

/**
 * Create a standalone draft invoice with an explicit payer and no work order
 * (#2427). POST /invoices — never send a bookingId.
 */
export function useCreateStandaloneInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: CreateStandaloneInvoiceInput) =>
      api.post<CreateStandaloneInvoiceResponse>('/invoices', body, {
        headers: { 'Idempotency-Key': crypto.randomUUID() },
      }),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.detail(created.id) });
    },
  });
}

/**
 * Record payment for invoice
 */
export function useRecordPayment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ invoiceId, workOrderId: _workOrderId, ...body }: RecordPaymentData) =>
      // Idempotency-Key guards against a retried request recording the payment
      // twice; the server replays the first response (#1034).
      api.post(`/invoices/${invoiceId}/payment`, body, {
        headers: { 'Idempotency-Key': crypto.randomUUID() },
      }),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.lists() });
      if (variables.workOrderId) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.detail(variables.workOrderId) });
      }
    },
  });
}

export function useCheckoutWorkOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      workOrderId,
      idempotencyKey,
      ...body
    }: CheckoutWorkOrderData) => api.post<CheckoutInvoiceResponse>(
      `/work-orders/${workOrderId}/invoice/checkout`,
      body,
      { headers: { 'Idempotency-Key': idempotencyKey } },
    ),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.lists() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.detail(variables.workOrderId) });
    },
  });
}

export function useCheckoutInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      invoiceId,
      workOrderId: _workOrderId,
      idempotencyKey,
      ...body
    }: CheckoutInvoiceData) => api.post<CheckoutInvoiceResponse>(
      `/invoices/${invoiceId}/checkout`,
      body,
      { headers: { 'Idempotency-Key': idempotencyKey } },
    ),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.lists() });
      if (variables.workOrderId) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.detail(variables.workOrderId) });
      }
    },
  });
}

/**
 * Open a PDF blob from `path` in a new tab. The popup is opened synchronously
 * so the click gesture still owns it when the download finishes.
 */
/**
 * A 200 that is not a PDF (an HTML fallback, an empty body) used to land in
 * the new tab as a blank page with a uuid title and no error anywhere (#3116).
 * Refuse it here so the caller closes the tab and shows a translated error.
 */
function assertPdfBlob(blob: Blob): void {
  if (blob.size === 0 || !blob.type.startsWith('application/pdf')) {
    throw new ApiError(502, 'Bad Gateway', { code: 'pdf_invalid', contentType: blob.type, size: blob.size });
  }
}

/**
 * `preOpenedWindow` lets a click handler that opens several documents open
 * every window synchronously first and hand each one in here (#4093): only
 * the first window.open after an await-free gesture is reliably allowed.
 */
async function openPdfInNewTab(path: string, preOpenedWindow?: Window | null): Promise<void> {
  const popup = preOpenedWindow !== undefined ? preOpenedWindow : window.open('', '_blank');
  try {
    const blob = await api.getBlob(path);
    assertPdfBlob(blob);
    const objectUrl = URL.createObjectURL(blob);
    if (popup) {
      popup.location.href = objectUrl;
    } else {
      const link = document.createElement('a');
      link.href = objectUrl;
      link.target = '_blank';
      link.click();
    }
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
  } catch (error) {
    popup?.close();
    throw error;
  }
}

/**
 * Open a preview of the invoice the work order would produce right now, rendered
 * straight from the work order without creating anything (#2095). Available
 * whether or not the work order is ready to invoice.
 */
export async function openInvoicePreviewPdf(workOrderId: string, taxPercent: number): Promise<void> {
  const params = new URLSearchParams();
  params.set('taxPercent', String(taxPercent));
  await openPdfInNewTab(`/work-orders/${workOrderId}/invoice/preview/pdf?${params.toString()}`);
}

export async function openReceiptPdf(receiptId: string, preOpenedWindow?: Window | null): Promise<void> {
  await openPdfInNewTab(`/receipts/${receiptId}/pdf`, preOpenedWindow);
}

/** Open the real finalized invoice PDF in a new tab (#1036). */
export async function openInvoicePdf(invoiceId: string, preOpenedWindow?: Window | null): Promise<void> {
  await openPdfInNewTab(`/invoices/${invoiceId}/pdf`, preOpenedWindow);
}

/**
 * Open the server PDF and trigger the browser print dialog (#1684).
 * 'left-open' means the PDF is open in a tab for the user to print themselves
 * (Firefox pdf.js, or print() refused); the caller should say so.
 */
export async function printInvoicePdf(invoiceId: string): Promise<PrintPdfResult> {
  return printPdfFromPath(`/invoices/${invoiceId}/pdf`);
}

/** The receipt straight to the printer for a counter customer (#3348). */
export async function printReceiptPdf(receiptId: string): Promise<PrintPdfResult> {
  return printPdfFromPath(`/receipts/${receiptId}/pdf`);
}

async function printPdfFromPath(path: string): Promise<PrintPdfResult> {
  const blob = await api.getBlob(path);
  assertPdfBlob(blob);
  const objectUrl = URL.createObjectURL(blob);
  const popup = window.open(objectUrl, '_blank');
  let result: PrintPdfResult = { status: 'left-open' };
  if (popup) {
    result = await tryPrintPdfWindow(popup);
  } else {
    const link = document.createElement('a');
    link.href = objectUrl;
    link.target = '_blank';
    link.click();
  }
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
  return result;
}

/**
 * Queue an audited email delivery of a finalized invoice (#1036). Idempotency
 * key prevents a retried request from sending two emails.
 */
export function useCreateSveaPayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ invoiceId }: { invoiceId: string }) =>
      api.post<{ success: true; sveaPaymentRequest: SveaPaymentRequest }>(
        `/invoices/${invoiceId}/svea-payment`,
        {},
      ),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.detail(variables.invoiceId) });
    },
  });
}

export function useSendSveaPayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      invoiceId,
      channel,
      recipient,
    }: {
      invoiceId: string;
      channel: 'email' | 'whatsapp';
      recipient?: string;
    }) =>
      api.post<{ success: true; sveaPaymentRequest: SveaPaymentRequest }>(
        `/invoices/${invoiceId}/svea-payment/send`,
        { channel, ...(recipient ? { recipient } : {}) },
      ),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.detail(variables.invoiceId) });
    },
  });
}

export interface DeliverInvoiceInput {
  invoiceId: string;
  workOrderId?: string;
  /** Default `email`; `einvoice` sends through the workshop's Maventa (#3071). */
  channel?: 'email' | 'einvoice';
  recipientEmail?: string;
  /** E-invoice address picked from Maventa's registry; default is the frozen recipient's. */
  recipientEia?: string;
  recipientOperator?: string;
}

export function useDeliverInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ invoiceId, workOrderId: _workOrderId, channel, recipientEmail, recipientEia, recipientOperator }: DeliverInvoiceInput) =>
      api.post<{ success: true; deliveryId: string; status: string; alreadyQueued: boolean }>(
        `/invoices/${invoiceId}/deliver`,
        channel === 'einvoice'
          ? {
              channel,
              ...(recipientEia ? { recipientEia } : {}),
              ...(recipientEia && recipientOperator ? { recipientOperator } : {}),
            }
          : recipientEmail ? { recipientEmail } : {},
        { headers: { 'Idempotency-Key': crypto.randomUUID() } },
      ),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.detail(variables.invoiceId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all });
    },
  });
}

/** Re-queue a failed invoice delivery (#1036). */
export function useRetryInvoiceDelivery() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ invoiceId, deliveryId }: { invoiceId: string; deliveryId: string }) =>
      api.post(`/invoices/${invoiceId}/deliveries/${deliveryId}/retry`, {}),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.detail(variables.invoiceId) });
    },
  });
}

/** Issue a full/partial credit note against a finalized invoice (#1037). */
export function useCreateCreditNote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ invoiceId, workOrderId: _workOrderId, lines, reason }: { invoiceId: string; workOrderId?: string; lines: CreditNoteLineInput[]; reason?: string }) =>
      api.post<{ success: true; creditNoteId: string; creditNoteNumber: string; amountCents: number; fullCredit: boolean }>(
        `/invoices/${invoiceId}/credit-note`,
        { lines, ...(reason ? { reason } : {}) },
      ),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.detail(variables.invoiceId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all });
      if (variables.workOrderId) {
        // A full credit may reopen the work order for invoicing (#4133): refetch its status,
        // the list row, its invoices and the preview so "Invoice" shows at once.
        void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.detail(variables.workOrderId) });
        void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.lists() });
        void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.forWorkOrder(variables.workOrderId) });
        void queryClient.invalidateQueries({ queryKey: ['workOrderInvoices', variables.workOrderId] });
        void queryClient.invalidateQueries({ queryKey: queryKeys.invoicePreview(variables.workOrderId) });
      }
    },
  });
}

/** Void a recorded payment by inserting a linked reversal row (#2342). */
export function useVoidInvoicePayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      invoiceId,
      paymentId,
      note,
    }: { invoiceId: string; paymentId: string; workOrderId?: string; note?: string }) =>
      api.post<{
        success: true;
        invoiceId: string;
        paidAmountCents: number;
        totalCents: number;
        remainingCents: number;
        status: string;
      }>(
        `/invoices/${invoiceId}/payments/${paymentId}/void`,
        { ...(note ? { note } : {}) },
      ),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.detail(variables.invoiceId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all });
      if (variables.workOrderId) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.detail(variables.workOrderId) });
        void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.forWorkOrder(variables.workOrderId) });
        void queryClient.invalidateQueries({ queryKey: ['workOrderInvoices', variables.workOrderId] });
        void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.lists() });
      }
    },
  });
}

/** Correct paymentReference / note on an original payment row (#2342). */
export function useUpdateInvoicePayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      invoiceId,
      paymentId,
      paymentReference,
      note,
    }: {
      invoiceId: string;
      paymentId: string;
      workOrderId?: string;
      paymentReference?: string | null;
      note?: string | null;
    }) =>
      api.patch<{ success: true; id: string; paymentReference: string | null; note: string | null }>(
        `/invoices/${invoiceId}/payments/${paymentId}`,
        {
          ...(paymentReference !== undefined ? { paymentReference } : {}),
          ...(note !== undefined ? { note } : {}),
        },
      ),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.detail(variables.invoiceId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all });
      if (variables.workOrderId) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.detail(variables.workOrderId) });
        void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.forWorkOrder(variables.workOrderId) });
      }
    },
  });
}

/**
 * Reverse a payment with a counter-entry (#1037 / #2812).
 * Bookkeeping only: never calls Svea and never moves money to the customer.
 */
export function useReversePayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ invoiceId, workOrderId: _workOrderId, amountCents, note }: { invoiceId: string; workOrderId?: string; amountCents: number; note?: string }) =>
      api.post(`/invoices/${invoiceId}/reverse-payment`, { amountCents, ...(note ? { note } : {}) }),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.detail(variables.invoiceId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all });
    },
  });
}

/**
 * Persistent Svea refund attempts for an invoice (#2812). Survives reload;
 * pending / pending_manual_transfer rows are not completed customer refunds.
 */
export function useSveaRefundAttempts(invoiceId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: sveaRefundAttemptsQueryKey(invoiceId ?? ''),
    queryFn: () => api.get<SveaRefundAttemptsResponse>(`/invoices/${invoiceId}/svea-refunds`),
    enabled: enabled && Boolean(invoiceId),
  });
}

/** Refund money to the customer (#1037 / #2812). Svea outcomes are 201/202. */
export function useRefundPayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      invoiceId,
      workOrderId: _workOrderId,
      amountCents,
      reason,
      method,
      pmtId,
    }: RefundPaymentMutationInput) =>
      api.post<RefundPaymentResponse>(
        `/invoices/${invoiceId}/refund`,
        {
          amountCents,
          ...(reason ? { reason } : {}),
          ...(method ? { method } : {}),
          ...(pmtId ? { pmtId } : {}),
        },
      ),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.detail(variables.invoiceId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all });
    },
  });
}

/**
 * Mark invoice as sent
 */
export function useMarkInvoiceSent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ invoiceId, workOrderId: _workOrderId }: { invoiceId: string; workOrderId?: string }) =>
      api.post(`/invoices/${invoiceId}/mark-sent`, {}),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.lists() });
      if (variables.workOrderId) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.detail(variables.workOrderId) });
      }
    },
  });
}

/**
 * Edit a draft invoice via PATCH /invoices/:id. Callers send `expectedVersion`
 * from GET; a 409 `stale_invoice_version` means refetch and retry (#2150).
 */
export function useUpdateDraftInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ invoiceId, workOrderId: _workOrderId, ...body }: UpdateDraftInvoiceData) =>
      api.patch<UpdateDraftInvoiceResponse>(`/invoices/${invoiceId}`, body),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.detail(variables.invoiceId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all });
      if (variables.workOrderId) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.detail(variables.workOrderId) });
      }
    },
  });
}

/**
 * Finalize a draft invoice (draft → sent) through a delivery channel. The
 * server verifies required data and freezes the recipient snapshot (#1035).
 */
export function useFinalizeInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      invoiceId,
      workOrderId: _workOrderId,
      channel,
    }: { invoiceId: string; workOrderId?: string; channel: InvoiceDeliveryChannel }) =>
      api.post<{ success: true; status: string; version: number }>(
        `/invoices/${invoiceId}/finalize`,
        { channel },
      ),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.detail(variables.invoiceId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.lists() });
      if (variables.workOrderId) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.detail(variables.workOrderId) });
      }
    },
  });
}

/**
 * Void invoice
 */
export function useVoidInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ invoiceId, reason, workOrderId: _workOrderId }: { invoiceId: string; reason: string; workOrderId?: string }) =>
      api.post(`/invoices/${invoiceId}/void`, { reason }),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.lists() });
      if (variables.workOrderId) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.detail(variables.workOrderId) });
      }
    },
  });
}
